import i18next from 'i18next'
import { CustomReportCategory } from './typesCategory'
import { TimePeriodOptions } from './types'
import { ReportDataType } from '../reports/types'
import { parseDate } from '../../../config/dayjsConfig'

const visitedReferences = new Set<string>()

export const keyFigureTrans = (c: CustomReportCategory) => {
  const keyFigureArr = Array.isArray(c.value) ? c.value : [c.value]
  let transLated: string = i18next.t(`keyFigureStatement:${keyFigureArr[0]}`)
  if (keyFigureArr.length > 1) {
    transLated += ` + ${keyFigureArr.length - 1}`
  }
  return transLated
}

export const formulaTrans = (c: CustomReportCategory) => {
  const formulaArr = Array.isArray(c.value) ? c.value : [c.value]
  let formulas: string = c.formulas?.[0]?.name || ''
  if (formulaArr.length > 1) {
    formulas += ` + ${formulaArr.length - 1}`
  }
  return formulas
}

export const defaultTitle = (
  type: CustomReportCategory['type'],
  value: string | string[] | number | number[] | undefined
) => {
  switch (type) {
    case 'company':
      return `{company}
`
    case 'dimension':
      return `{dimensionName}
`
    case 'keyFigureId':
      return `{keyFigure}
`
    case 'formulaId':
      return `{formula}
`
    case 'reference':
      return '{reference}'
    case 'periodGroup':
      switch (value) {
        case TimePeriodOptions.CustomPeriod:
        case TimePeriodOptions.Past12months:
        case TimePeriodOptions.FiscalyearToDate:
        case TimePeriodOptions.PreviousFiscalyear:
        case TimePeriodOptions.PreviousFiscalyearToDate:
        case TimePeriodOptions.Currentfiscalyear:
        case TimePeriodOptions.PreviousYear:
        case TimePeriodOptions.Previousyeartodate:
        case TimePeriodOptions.YearToDate:
        case TimePeriodOptions.FromToDate:
        case TimePeriodOptions.Quarter:
        case TimePeriodOptions.CumulativeFiscalyear:
          return `{mm/yy} - {mm/yy}
{dataType}
`
        case TimePeriodOptions.CustomMonth:
        case TimePeriodOptions.FiscalyearFirstMonth:
        case TimePeriodOptions.CurrentMonth:
          return `{mm/yy}
{dataType}
`
        case TimePeriodOptions.CurrentYear:
        case TimePeriodOptions.CustomYear:
          return `{yyyy}
{dataType}
`
        case TimePeriodOptions.CurrentDay:
          return `{dd/mm/yyyy}
{dataType}
`
        case TimePeriodOptions.CurrentWeek:
          return `{w/yyyy}
{dataType}
`
        default:
          return ''
      }
    case 'function':
      return `{function}
`

    default:
      return ''
  }
}

const rulesOther: {
  [key: string]: (e: CustomReportCategory, categories?: CustomReportCategory[], noStyles?: boolean) => string
} = {
  '{dimensionName}': (e: CustomReportCategory) => e.dimension?.name || '',
  '{dataType}': (e: CustomReportCategory, categories?: CustomReportCategory[], noStyles = false) =>
    noStyles
      ? i18next.t(`global:${e.dataType || ReportDataType.actuals}`)
      : `<span style="font-size: 90%; opacity: 0.7;">${i18next.t(
          `global:${e.dataType || ReportDataType.actuals}`
        )}</span>`,
  '{scenarioName}': (e: CustomReportCategory) => e.budgetingScenario?.name || '',
  '{company}': (e: CustomReportCategory) => e.company?.name || '',
  '{formula}': (e: CustomReportCategory) => formulaTrans(e),
  '{keyFigure}': (e: CustomReportCategory) => keyFigureTrans(e),
  '{reference}': (e: CustomReportCategory, categories?: CustomReportCategory[], noStyles = false) =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getReferenceTitle(e, categories, noStyles),
  '{function}': (e: CustomReportCategory, categories?: CustomReportCategory[], noStyles = false) =>
    noStyles
      ? i18next.t(`functionCategory:${e.value}`)
      : `<span style="opacity: 0.7;">${i18next.t(`functionCategory:${e.value}`)}</span>`
}

const rulesDate: { [key: string]: (e: string) => string } = {
  '{yyyy}': (e: string) => parseDate(e)?.format('YYYY') || '',
  '{mm/yyyy}': (e: string) => parseDate(e)?.format('MM/YYYY') || '',
  '{mm/yy}': (e: string) => parseDate(e)?.format('MM/YY') || '',
  '{dd/mm/yyyy}': (e: string) => parseDate(e)?.format('DD/MM/YYYY') || '',
  '{dd}': (e: string) => parseDate(e)?.format('dd') || '',
  '{dddd}': (e: string) => parseDate(e)?.format('dddd') || '',
  '{w}': (e: string) => parseDate(e)?.format('w') || '',
  '{w/yyyy}': (e: string) => {
    const parsed = parseDate(e)
    return parsed ? `W${parsed.format('w')}/${parsed.format('YYYY')}` : ''
  }
}

const arrayDate = ['startDate', 'endDate']

const transformOtherPlaceholder = (
  placeholderString: string,
  c: CustomReportCategory,
  categories?: CustomReportCategory[],
  noStyles = false
) => {
  return rulesOther[placeholderString](c, categories, noStyles)
}

const extractPlaceholders = (title: string, rules: { [key: string]: (e: any) => string }) => {
  const keys = Object.keys(rules).map(key => key.replace(/[{}]/g, '\\$&'))
  const regex = new RegExp(keys.join('|'), 'g')
  return title.match(regex) || []
}

export const transform = (
  t: string,
  c: CustomReportCategory,
  categories?: CustomReportCategory[],
  noStyles = false
) => {
  let title = t
  for (let i = 0; i < arrayDate.length; i += 1) {
    const stringArrDates = extractPlaceholders(title, rulesDate)
    if (stringArrDates[0] && rulesDate[stringArrDates[0]]) {
      title = title.replace(
        stringArrDates[0],
        rulesDate[stringArrDates[0]](c[arrayDate[i] as keyof CustomReportCategory['startDate' | 'endDate']])
      )
    }
  }

  for (const otherPlaceholder of extractPlaceholders(title, rulesOther)) {
    title = title.replace(otherPlaceholder, transformOtherPlaceholder(otherPlaceholder, c, categories, noStyles))
  }

  return title
}

export const transformTitle = (c: CustomReportCategory, categories?: CustomReportCategory[], noStyles = false) => {
  if (!c) return ''
  let transformedTitle = transform(c.title ?? defaultTitle(c.type, c.value), c, categories, noStyles)

  const processChildren = (category: CustomReportCategory): string => {
    let result = ''
    if (category.children) {
      for (const child of category.children) {
        result += transform(` ${child?.title ?? defaultTitle(child.type, child.value)}`, child, categories, noStyles)
        if (child?.children) {
          result += processChildren(child)
        }
      }
    }
    return result
  }
  if ((c.type !== 'company' && c.type !== 'dimension') || c.value === 'sum') {
    transformedTitle += processChildren(c)
  }
  return transformedTitle
}

const findCategoryById = (id: string, categories?: CustomReportCategory[]): CustomReportCategory | null => {
  if (!categories?.length || !id) return null

  for (const cat of categories) {
    if (cat.id === id) return cat

    const foundInChildren = cat.children ? findCategoryById(id, cat.children) : null

    if (foundInChildren) return foundInChildren
  }

  return null
}

// Helper function to find the path from root to a target category
const findPathToCategory = (
  targetId: string,
  categories?: CustomReportCategory[],
  currentPath: CustomReportCategory[] = []
): CustomReportCategory[] | null => {
  if (!categories?.length || !targetId) return null

  for (const cat of categories) {
    // Try this path
    const newPath = [...currentPath, cat]

    // If this is the target category, return the path
    if (cat.id === targetId) {
      return newPath
    }

    // Check in children
    if (cat.children?.length) {
      const pathInChildren = findPathToCategory(targetId, cat.children, newPath)
      if (pathInChildren) {
        return pathInChildren
      }
    }
  }

  return null
}

const transformForReference = (
  title: string,
  c: CustomReportCategory,
  categories?: CustomReportCategory[],
  noStyles = false
) => {
  let result = title

  // Only handle date placeholders and simple replacements, avoiding recursive calls
  for (let i = 0; i < arrayDate.length; i += 1) {
    const stringArrDates = extractPlaceholders(result, rulesDate)
    if (stringArrDates[0] && rulesDate[stringArrDates[0]]) {
      result = result.replace(
        stringArrDates[0],
        rulesDate[stringArrDates[0]](c[arrayDate[i] as keyof CustomReportCategory['startDate' | 'endDate']])
      )
    }
  }

  // Handle only simple placeholders, avoiding {reference} placeholder which causes recursion
  const safePlaceholders = Object.keys(rulesOther).filter(key => key !== '{reference}')
  for (const placeholder of safePlaceholders) {
    if (result.includes(placeholder)) {
      result = result.replace(placeholder, rulesOther[placeholder](c, categories, noStyles))
    }
  }

  return result
}

export const getReferenceTitle = (c: CustomReportCategory, categories?: CustomReportCategory[], noStyles = false) => {
  // Guard against infinite loops with circular references
  if (c?.type === 'reference' && c.value) {
    const refId = c.value as string
    if (visitedReferences.has(refId)) {
      return `[Circular Reference]`
    }
    visitedReferences.add(refId)
  }

  try {
    // If this is a reference type, find the referenced category
    let targetCategory = c
    if (c?.type === 'reference' && c.value && categories) {
      const referencedCategory = findCategoryById(c.value as string, categories)
      if (referencedCategory) {
        targetCategory = referencedCategory
      } else {
        // If we can't find the referenced category, return a simple fallback
        if (c?.type === 'reference' && c.value) {
          visitedReferences.delete(c.value as string)
        }
        return `[Missing Reference: ${c.value}]`
      }
    }

    // Find the path from root to the target category
    let fullPath: CustomReportCategory[] | null = null

    if (targetCategory?.id && categories) {
      fullPath = findPathToCategory(targetCategory.id, categories)
    }

    // Build the title from the full path without using the regular transform function
    let title = ''

    if (fullPath?.length) {
      // Start with the root category title - using our safe transform function
      const rootTitle = fullPath[0].title || defaultTitle(fullPath[0].type, fullPath[0].value)
      title = `${transformForReference(rootTitle, fullPath[0], categories, noStyles)} `

      // Add all other categories in the path - using our safe transform function
      for (let i = 1; i < fullPath.length; i += 1) {
        const cat = fullPath[i]
        const catTitle = cat.title || defaultTitle(cat.type, cat.value)
        title += `${transformForReference(catTitle, cat, categories, noStyles)} `
      }
    } else {
      // Fallback to just using the current category's title - using our safe transform function
      const currentTitle = targetCategory?.title || defaultTitle(targetCategory?.type, targetCategory?.value)
      title = transformForReference(currentTitle, targetCategory, categories, noStyles)
    }

    // Clean up: remove any tracked references after we're done
    if (c?.type === 'reference' && c.value) {
      visitedReferences.delete(c.value as string)
    }

    return title.trim()
  } catch (error) {
    // In case of any error, clean up and return a fallback
    visitedReferences.clear()
    console.error('Error in getReferenceTitle:', error)
    return c?.title || defaultTitle(c?.type, c?.value) || '[Error]'
  }
}
